import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Battery, Gauge, Zap, ChevronLeft, ChevronRight } from 'lucide-react';

export default function HeroSection({ pageContent, settings, isLoading }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const preloadedImages = useRef({});
  
  // Get hero images from settings
  const heroImages = settings?.hero_slider_images?.length > 0 
    ? settings.hero_slider_images 
    : [];
  
  const autoSlideInterval = settings?.hero_slider_interval || 5000;
  const autoSlideEnabled = settings?.hero_slider_enabled !== false;

  // Preload images efficiently
  useEffect(() => {
    if (heroImages.length === 0) return;
    
    // Only preload first image immediately, others after
    const firstImg = new Image();
    firstImg.onload = () => setImagesLoaded(true);
    firstImg.src = heroImages[0]?.url;
    preloadedImages.current[heroImages[0]?.url] = firstImg;
    
    // Preload rest after first paint
    if (heroImages.length > 1) {
      requestIdleCallback(() => {
        heroImages.slice(1).forEach((img) => {
          if (!preloadedImages.current[img.url]) {
            const image = new Image();
            image.src = img.url;
            preloadedImages.current[img.url] = image;
          }
        });
      }, { timeout: 2000 });
    }
  }, [heroImages]);

  // Auto slide
  const nextSlide = useCallback(() => {
    if (heroImages.length > 1) {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }
  }, [heroImages.length]);

  const prevSlide = () => {
    if (heroImages.length > 1) {
      setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
    }
  };

  useEffect(() => {
    if (!autoSlideEnabled || heroImages.length <= 1) return;
    const interval = setInterval(nextSlide, autoSlideInterval);
    return () => clearInterval(interval);
  }, [autoSlideEnabled, autoSlideInterval, heroImages.length, nextSlide]);

  // Get content from pageContent or defaults
  const label = pageContent?.hero_label || 'Sales Resmi BYD';
  const title = pageContent?.hero_title || 'Masa Depan Mobilitas';
  const subtitle = pageContent?.hero_subtitle || 'Dimulai Sekarang';
  const description = pageContent?.hero_description || 'Temukan kenyamanan berkendara mobil listrik BYD dengan teknologi Blade Battery yang aman dan efisien.';

  return (
    <section className="hero-section" data-testid="hero-section" role="banner" aria-label="Hero section">
      {/* Background - Only render current image for performance */}
      <div className="absolute inset-0 overflow-hidden bg-slate-900" aria-hidden="true">
        {heroImages.length > 0 && (
          <img
            src={heroImages[currentSlide]?.url}
            alt={heroImages[currentSlide]?.alt || 'BYD Electric Vehicle'}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            fetchpriority="high"
            decoding="sync"
          />
        )}
        <div className="hero-overlay" />
      </div>

      {/* Slider Controls - Only show if multiple images */}
      {heroImages.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
            aria-label="Slide sebelumnya"
          >
            <ChevronLeft className="w-6 h-6 text-white" aria-hidden="true" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
            aria-label="Slide selanjutnya"
          >
            <ChevronRight className="w-6 h-6 text-white" aria-hidden="true" />
          </button>
          
          {/* Slide Indicators */}
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20 flex gap-2" role="tablist" aria-label="Slide navigation">
            {heroImages.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`w-3 h-3 rounded-full transition-all min-w-[12px] min-h-[12px] ${
                  idx === currentSlide ? 'w-8 bg-white' : 'bg-white/50'
                }`}
                aria-label={`Ke slide ${idx + 1}`}
                aria-selected={idx === currentSlide}
                role="tab"
              />
            ))}
          </div>
        </>
      )}

      {/* Content */}
      <div className="byd-container relative z-10 h-full flex flex-col justify-end pb-8 lg:pb-16">
        <div className="max-w-2xl animate-fadeInUp">
          <span className="text-white/80 text-sm font-medium tracking-wider uppercase mb-4 block">
            {label}
          </span>
          
          <h1 className="heading-display text-4xl sm:text-5xl lg:text-6xl text-white mb-2">
            {title}
          </h1>
          <h2 className="heading-display text-4xl sm:text-5xl lg:text-6xl text-gradient mb-6">
            {subtitle}
          </h2>
          
          <p className="text-white/70 text-base lg:text-lg mb-6 max-w-lg">
            {description}
          </p>

          {/* Quick Specs */}
          <div className="flex flex-wrap gap-4 mb-8">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <Battery className="w-4 h-4 text-green-400" aria-hidden="true" />
              <span className="text-sm text-white">Blade Battery</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <Gauge className="w-4 h-4 text-blue-400" aria-hidden="true" />
              <span className="text-sm text-white">420+ km Range</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <Zap className="w-4 h-4 text-yellow-400" aria-hidden="true" />
              <span className="text-sm text-white">Fast Charging</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4">
            <Link 
              to="/mobil/byd-m6"
              className="btn-hero-primary btn-pulse flex items-center gap-2"
              data-testid="hero-cta-m6"
            >
              Lihat BYD M6
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            <Link 
              to="/mobil/byd-atto-1"
              className="btn-hero-secondary flex items-center gap-2"
              data-testid="hero-cta-atto1"
            >
              Lihat BYD Atto 1
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
